import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { AugmentedFeedback } from "@productlane/api/src/lib/timeline-events"
import { getTimelineEvents } from "@productlane/api/src/browser"
import { queries, threadDetailQueryArgs } from "@productlane/zero/queries"

import { api } from "@/lib/api"
import { useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import { useThreadContext } from "../thread-context"

export function usePreloadTimelineEvents(threadId: string | undefined) {
	const zero = useZero()
	const decodedToken = useAuthData()

	// Preload immediately — Zero dedupes by query hash, so it's safe to fire
	// on every render of a visible list item. Keeping queries warm here is
	// what makes keyboard nav feel instant: by the time the user navigates,
	// threadWithFullDetails for that id is already synced.
	useEffect(() => {
		if (!threadId) return
		const cleanup = zero.preload(
			queries.threadWithFullDetails(threadDetailQueryArgs(threadId)),
			{
				ttl: "1m",
			},
		)
		return () => {
			cleanup.cleanup()
		}
	}, [zero, threadId, decodedToken])
}

/**
 * Builds the conversation you see in a thread view.
 *
 * A thread stores its SlackMessages, emails, comments, etc. in separate
 * database tables. This hook combines them into one chronologically sorted list
 * of `TimelineEvent`s. Each one is a single item in the conversation.
 * A slack message. An email. A comment. A system event. Etc.
 *
 * It also enriches each event with sender info (most importantly name and avatar) by looking up more info about
 * the sender in the database (joining against slackUsers, contacts, workspace members, etc.)
 *
 * Everything here runs against local Zero data (already synced to IndexedDB).
 * This hook returns all events it can build from what's synced so far;
 * `useGaplessEvents` then decides how many to actually show based on how
 * far along the sync is.
 */
/** Each sender address as written and lowercased, since stored contact emails vary in case. */
export function threadSenderEmails(
	thread: { emails?: readonly { from: unknown }[] } | null | undefined,
): string[] {
	const emails = new Set<string>()
	for (const email of thread?.emails ?? []) {
		const from = email.from as { email?: unknown } | null
		if (typeof from?.email !== "string" || !from.email.trim()) continue
		emails.add(from.email.trim())
		emails.add(from.email.trim().toLowerCase())
	}
	return [...emails].sort().slice(0, 200)
}

export function useTimelineEvents(threadOverride?: AugmentedFeedback) {
	const { thread: contextThread } = useThreadContext()
	const thread = threadOverride ?? contextThread
	const teamMembers = api.workspace.members.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})

	const [slackUsers] = useQuery(queries.slackUsers())
	const [linearUsers] = useQuery(queries.linearUsers())
	// Only the people this thread names: its contact and its email senders.
	const senderEmails = useMemo(() => threadSenderEmails(thread), [thread])
	const [threadContact] = useQuery(
		queries.contactById(thread?.contactId ?? ""),
		{
			enabled: !!thread?.contactId,
		},
	)
	const [senderContacts] = useQuery(queries.contactsByEmails(senderEmails), {
		enabled: senderEmails.length > 0,
	})
	const contacts = useMemo(
		() =>
			threadContact && !threadContact.isDeleted
				? [threadContact, ...senderContacts]
				: [...senderContacts],
		[threadContact, senderContacts],
	)
	const workspace = useZeroWorkspace()

	// Force re-render when the SLA deadline passes so the synthetic breach event appears
	const [slaTriggered, setSlaTriggered] = useState(0)
	useEffect(() => {
		const deadline = thread?.slaDeadlineAt
		if (!deadline || deadline === 0) return
		const remaining = deadline - Date.now()
		if (remaining <= 0) return // already past, synthetic event will show immediately
		const timer = setTimeout(() => setSlaTriggered((n) => n + 1), remaining)
		return () => clearTimeout(timer)
	}, [thread?.slaDeadlineAt])

	const events = useMemo(() => {
		const result = getTimelineEvents({
			thread,
			contacts,
			slackUsers,
			linearUsers,
			users: teamMembers.data?.map((m) => m.user) ?? [],
			workspace,
		})
		return result
		// NEEDED FOR THE COMPUTED SLA BREACH EVENT
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		thread,
		slackUsers,
		linearUsers,
		contacts,
		teamMembers.data,
		workspace,
		slaTriggered,
	])

	return events
}
